import React, { useState, useRef } from "react";
import { motion } from "framer-motion";
import { format } from "date-fns";
import { MapPin, Phone, Mail, Clock, ArrowUpRight, CalendarIcon, Loader2, ImagePlus, X } from "lucide-react";
import { toast } from "sonner";
import { CONTACT } from "@/constants/testIds";
import { submitQuote, submitConsultation, uploadPhotos, API } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "@/components/ui/select";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";

const services = [
  "Omni RGB Lights",
  "Permanent Home Lighting",
  "Commercial Installation",
  "Holiday Lighting",
  "Yard & Landscape",
  "Omni Maintenance Contract",
  "Other",
];

const times = ["9:00 AM", "11:00 AM", "1:00 PM", "3:00 PM", "5:00 PM"];

const fieldClass =
  "bg-[#0C1017] border-white/10 text-white placeholder:text-white/30 focus-visible:ring-[color:var(--primary)] focus-visible:ring-offset-0 rounded-none";
const labelClass = "font-mono-accent text-[10px] tracking-[0.3em] uppercase text-white/50";

export default function Contact({ activeTab, setActiveTab }) {
  const [quoting, setQuoting] = useState(false);
  const [booking, setBooking] = useState(false);
  const [bookDate, setBookDate] = useState(null);
  const [quotePhotos, setQuotePhotos] = useState([]); // {url, absoluteUrl, filename}
  const [bookPhotos, setBookPhotos] = useState([]);
  const [uploadingQuote, setUploadingQuote] = useState(false);
  const [uploadingBook, setUploadingBook] = useState(false);
  const quoteInputRef = useRef(null);
  const bookInputRef = useRef(null);

  const handleFiles = async (files, target) => {
    const list = Array.from(files || []);
    if (list.length === 0) return;
    const current = target === "quote" ? quotePhotos : bookPhotos;
    if (current.length + list.length > 8) {
      toast.error("You can upload up to 8 photos.");
      return;
    }
    const oversize = list.find((f) => f.size > 8 * 1024 * 1024);
    if (oversize) {
      toast.error(`"${oversize.name}" is over 8MB.`);
      return;
    }
    try {
      target === "quote" ? setUploadingQuote(true) : setUploadingBook(true);
      const uploaded = await uploadPhotos(list);
      if (target === "quote") setQuotePhotos((p) => [...p, ...uploaded]);
      else setBookPhotos((p) => [...p, ...uploaded]);
      toast.success(`${uploaded.length} photo${uploaded.length > 1 ? "s" : ""} attached.`);
    } catch (e) {
      toast.error("Photo upload failed. Please try again.");
    } finally {
      target === "quote" ? setUploadingQuote(false) : setUploadingBook(false);
    }
  };

  const removePhoto = (idx, target) => {
    if (target === "quote") setQuotePhotos((p) => p.filter((_, i) => i !== idx));
    else setBookPhotos((p) => p.filter((_, i) => i !== idx));
  };

  const handleQuote = async (e) => {
    e.preventDefault();
    const fd = new FormData(e.currentTarget);
    const payload = {
      name: fd.get("name")?.toString().trim(),
      email: fd.get("email")?.toString().trim(),
      phone: fd.get("phone")?.toString().trim(),
      service: fd.get("service")?.toString(),
      property_type: fd.get("property_type")?.toString(),
      address: fd.get("address")?.toString(),
      message: fd.get("message")?.toString(),
      photos: quotePhotos.map((p) => p.url),
    };
    if (!payload.name || !payload.email || !payload.phone || !payload.service) {
      toast.error("Please fill in name, email, phone and service.");
      return;
    }
    setQuoting(true);
    try {
      await submitQuote(payload);
      toast.success("Quote request sent — we'll be in touch within 24 hours.");
      e.target.reset();
      setQuotePhotos([]);
    } catch (err) {
      toast.error("Something went wrong. Please call us at 608-547-0711.");
    } finally {
      setQuoting(false);
    }
  };

  const handleBook = async (e) => {
    e.preventDefault();
    const fd = new FormData(e.currentTarget);
    if (!bookDate) {
      toast.error("Please choose a preferred date.");
      return;
    }
    const payload = {
      name: fd.get("name")?.toString().trim(),
      email: fd.get("email")?.toString().trim(),
      phone: fd.get("phone")?.toString().trim(),
      preferred_date: format(bookDate, "yyyy-MM-dd"),
      preferred_time: fd.get("time")?.toString(),
      service_interest: fd.get("service")?.toString(),
      notes: fd.get("notes")?.toString(),
      photos: bookPhotos.map((p) => p.url),
    };
    if (!payload.name || !payload.email || !payload.phone) {
      toast.error("Please fill in name, email and phone.");
      return;
    }
    setBooking(true);
    try {
      await submitConsultation(payload);
      toast.success(`Consultation booked for ${payload.preferred_date}. See you then.`);
      e.target.reset();
      setBookDate(null);
      setBookPhotos([]);
    } catch (err) {
      toast.error("Booking failed. Please try again or call 608-547-0711.");
    } finally {
      setBooking(false);
    }
  };

  return (
    <section id="contact" data-testid={CONTACT.root} className="section-pad relative border-t border-white/10">
      <div className="container-x">
        <div className="grid md:grid-cols-[220px_1fr] gap-10 md:gap-20 mb-14">
          <div className="font-mono-accent text-[11px] tracking-[0.35em] uppercase text-[color:var(--primary)]">
            [ Contact ]
          </div>
          <h2 className="font-display uppercase text-white text-[clamp(2.25rem,5.5vw,4.5rem)] leading-[0.95] tracking-tight max-w-3xl">
            Light it once.<br />
            <span className="text-[color:var(--primary)]">Start the conversation.</span>
          </h2>
        </div>

        <div className="grid lg:grid-cols-[400px_1fr] gap-10 lg:gap-16">
          {/* Left: Contact card */}
          <motion.aside
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.7 }}
            className="space-y-8"
          >
            <div className="border border-white/10 p-8 bg-[#0C1017]">
              <div className={labelClass + " mb-4"}>Studio</div>
              <div className="space-y-5 text-white">
                <div className="flex items-start gap-3">
                  <MapPin size={18} className="text-[color:var(--primary)] mt-1 flex-shrink-0" />
                  <div>
                    <div className="font-display uppercase text-xl tracking-tight leading-tight">420 Golden Ave</div>
                    <div className="text-white/60 text-sm mt-1">Wisconsin Dells, WI</div>
                  </div>
                </div>
                <a href="tel:+16085470711" className="flex items-start gap-3 group">
                  <Phone size={18} className="text-[color:var(--primary)] mt-1 flex-shrink-0" />
                  <div>
                    <div className="font-display uppercase text-xl tracking-tight leading-tight group-hover:text-[color:var(--primary)] transition-colors">
                      608 · 547 · 0711
                    </div>
                    <div className="text-white/60 text-sm mt-1">Mon–Fri · 8am–5pm</div>
                  </div>
                </a>
                <a href="mailto:younggunslighting@gmail.com" className="flex items-start gap-3 group">
                  <Mail size={18} className="text-[color:var(--primary)] mt-1 flex-shrink-0" />
                  <div>
                    <div className="font-display uppercase text-lg tracking-tight leading-tight group-hover:text-[color:var(--primary)] transition-colors">
                      younggunslighting@gmail.com
                    </div>
                    <div className="text-white/60 text-sm mt-1">We reply within one business day</div>
                  </div>
                </a>
                <div className="flex items-start gap-3">
                  <Clock size={18} className="text-[color:var(--primary)] mt-1 flex-shrink-0" />
                  <div>
                    <div className="text-white text-sm">Mon – Fri · 8:00 AM – 5:00 PM</div>
                    <div className="text-white/60 text-sm">Sat by appointment · Sun closed</div>
                  </div>
                </div>
              </div>
            </div>

            <div className="border border-[color:var(--primary)]/40 p-6 bg-[color:var(--primary)]/[0.04]">
              <div className={labelClass + " mb-3"}>Certified</div>
              <div className="flex items-center gap-4">
                <div className="rounded-full bg-white p-1.5 shadow-[0_0_25px_rgba(0,229,255,0.3)]">
                  <img
                    src="https://customer-assets-v7afamib.emergentagent.net/job_bright-installations-1/artifacts/b35qdpuq_Omni%20Certified%20Installer%20Logo%20%281%29.png"
                    alt="Omni Certified Installer"
                    className="h-14 w-14 object-contain"
                  />
                </div>
                <div>
                  <div className="font-display uppercase text-white text-xl leading-tight tracking-tight">
                    Omni RGB<br />Certified Installer
                  </div>
                  <div className="text-white/60 text-xs mt-1">Factory-trained. Fully warrantied.</div>
                </div>
              </div>
            </div>
          </motion.aside>

          {/* Right: Tabs (Quote / Book) */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.7 }}
            className="border border-white/10 bg-[#0C1017] p-6 md:p-10"
          >
            <Tabs value={activeTab} onValueChange={setActiveTab}>
              <TabsList className="grid grid-cols-2 bg-transparent border border-white/10 p-1 rounded-none mb-8 h-auto">
                <TabsTrigger
                  data-testid={CONTACT.tabQuote}
                  value="quote"
                  className="data-[state=active]:bg-[color:var(--primary)] data-[state=active]:text-black text-white font-mono-accent text-[11px] tracking-[0.25em] uppercase rounded-none py-3"
                >
                  Request a Quote
                </TabsTrigger>
                <TabsTrigger
                  data-testid={CONTACT.tabBook}
                  value="book"
                  className="data-[state=active]:bg-[color:var(--primary)] data-[state=active]:text-black text-white font-mono-accent text-[11px] tracking-[0.25em] uppercase rounded-none py-3"
                >
                  Book Consultation
                </TabsTrigger>
              </TabsList>

              {/* Quote Form */}
              <TabsContent value="quote">
                <form data-testid={CONTACT.quoteForm} onSubmit={handleQuote} className="space-y-6">
                  <div className="grid md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <Label className={labelClass} htmlFor={CONTACT.quoteName}>Name</Label>
                      <Input data-testid={CONTACT.quoteName} id={CONTACT.quoteName} name="name" placeholder="Jordan Smith" className={fieldClass} />
                    </div>
                    <div className="space-y-2">
                      <Label className={labelClass} htmlFor={CONTACT.quoteEmail}>Email</Label>
                      <Input data-testid={CONTACT.quoteEmail} id={CONTACT.quoteEmail} name="email" type="email" placeholder="you@email.com" className={fieldClass} />
                    </div>
                    <div className="space-y-2">
                      <Label className={labelClass} htmlFor={CONTACT.quotePhone}>Phone</Label>
                      <Input data-testid={CONTACT.quotePhone} id={CONTACT.quotePhone} name="phone" placeholder="(608) 555 0123" className={fieldClass} />
                    </div>
                    <div className="space-y-2">
                      <Label className={labelClass}>Service</Label>
                      <Select name="service" defaultValue="Omni RGB Lights">
                        <SelectTrigger data-testid={CONTACT.quoteService} className={fieldClass}>
                          <SelectValue placeholder="Select a service" />
                        </SelectTrigger>
                        <SelectContent className="bg-[#0C1017] border-white/10 text-white">
                          {services.map((s) => (
                            <SelectItem key={s} value={s} className="focus:bg-[color:var(--primary)]/10 focus:text-white">
                              {s}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <input type="hidden" name="service" />
                    </div>
                    <div className="space-y-2">
                      <Label className={labelClass}>Property Type</Label>
                      <Select name="property_type" defaultValue="Residential">
                        <SelectTrigger data-testid={CONTACT.quotePropertyType} className={fieldClass}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="bg-[#0C1017] border-white/10 text-white">
                          {["Residential", "Commercial", "Multi-family", "Municipal"].map((s) => (
                            <SelectItem key={s} value={s} className="focus:bg-[color:var(--primary)]/10 focus:text-white">
                              {s}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label className={labelClass} htmlFor={CONTACT.quoteAddress}>Address</Label>
                      <Input data-testid={CONTACT.quoteAddress} id={CONTACT.quoteAddress} name="address" placeholder="Street, City, State" className={fieldClass} />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label className={labelClass} htmlFor={CONTACT.quoteMessage}>Project details</Label>
                    <Textarea
                      data-testid={CONTACT.quoteMessage}
                      id={CONTACT.quoteMessage}
                      name="message"
                      rows={4}
                      placeholder="Roofline length, existing wiring, timeline, anything else we should know..."
                      className={fieldClass}
                    />
                  </div>

                  {/* Photo uploader */}
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <Label className={labelClass}>Photos (optional)</Label>
                      <span className="font-mono-accent text-[10px] tracking-[0.25em] uppercase text-white/40">
                        {quotePhotos.length}/8
                      </span>
                    </div>
                    <div
                      onClick={() => quoteInputRef.current?.click()}
                      onDragOver={(e) => e.preventDefault()}
                      onDrop={(e) => { e.preventDefault(); handleFiles(e.dataTransfer.files, "quote"); }}
                      className="border border-dashed border-white/15 hover:border-[color:var(--primary)]/60 bg-[#05070A] p-6 text-center cursor-pointer transition-colors"
                      data-testid="quote-photo-drop"
                    >
                      <input
                        ref={quoteInputRef}
                        type="file"
                        accept="image/*"
                        multiple
                        className="hidden"
                        data-testid="quote-photo-input"
                        onChange={(e) => { handleFiles(e.target.files, "quote"); e.target.value = ""; }}
                      />
                      {uploadingQuote ? (
                        <div className="flex items-center justify-center gap-2 text-white/70 text-sm">
                          <Loader2 className="animate-spin" size={16} /> Uploading…
                        </div>
                      ) : (
                        <div className="flex flex-col items-center gap-2">
                          <ImagePlus size={22} className="text-[color:var(--primary)]" />
                          <div className="text-white/70 text-sm">Drop photos or click to upload</div>
                          <div className="font-mono-accent text-[10px] tracking-[0.25em] uppercase text-white/40">
                            JPG · PNG · WEBP · Max 8MB each
                          </div>
                        </div>
                      )}
                    </div>
                    {quotePhotos.length > 0 && (
                      <div className="grid grid-cols-4 md:grid-cols-6 gap-2">
                        {quotePhotos.map((p, i) => (
                          <div key={p.filename} className="relative group aspect-square border border-white/10 overflow-hidden" data-testid={`quote-photo-thumb-${i}`}>
                            <img src={p.absoluteUrl} alt="" className="w-full h-full object-cover" />
                            <button
                              type="button"
                              onClick={() => removePhoto(i, "quote")}
                              className="absolute top-1 right-1 p-1 bg-black/70 border border-white/20 text-white hover:bg-[color:var(--primary)] hover:text-black transition-colors"
                              aria-label="Remove"
                              data-testid={`quote-photo-remove-${i}`}
                            >
                              <X size={12} />
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  <button
                    data-testid={CONTACT.quoteSubmit}
                    type="submit"
                    disabled={quoting}
                    className="w-full md:w-auto inline-flex items-center gap-2 px-8 py-4 bg-[color:var(--primary)] text-black font-mono-accent text-[11px] tracking-[0.25em] uppercase hover:bg-white transition-colors btn-sharp disabled:opacity-60"
                  >
                    {quoting ? <><Loader2 className="animate-spin" size={14}/> Sending</> : <>Send Quote Request <ArrowUpRight size={14} /></>}
                  </button>
                </form>
              </TabsContent>

              {/* Booking Form */}
              <TabsContent value="book">
                <form data-testid={CONTACT.bookForm} onSubmit={handleBook} className="space-y-6">
                  <div className="grid md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <Label className={labelClass} htmlFor={CONTACT.bookName}>Name</Label>
                      <Input data-testid={CONTACT.bookName} id={CONTACT.bookName} name="name" placeholder="Jordan Smith" className={fieldClass} />
                    </div>
                    <div className="space-y-2">
                      <Label className={labelClass} htmlFor={CONTACT.bookEmail}>Email</Label>
                      <Input data-testid={CONTACT.bookEmail} id={CONTACT.bookEmail} name="email" type="email" placeholder="you@email.com" className={fieldClass} />
                    </div>
                    <div className="space-y-2">
                      <Label className={labelClass} htmlFor={CONTACT.bookPhone}>Phone</Label>
                      <Input data-testid={CONTACT.bookPhone} id={CONTACT.bookPhone} name="phone" placeholder="(608) 555 0123" className={fieldClass} />
                    </div>
                    <div className="space-y-2">
                      <Label className={labelClass}>Service interest</Label>
                      <Select name="service" defaultValue="Omni RGB Lights">
                        <SelectTrigger data-testid={CONTACT.bookService} className={fieldClass}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="bg-[#0C1017] border-white/10 text-white">
                          {services.map((s) => (
                            <SelectItem key={s} value={s} className="focus:bg-[color:var(--primary)]/10 focus:text-white">
                              {s}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label className={labelClass}>Preferred date</Label>
                      <Popover>
                        <PopoverTrigger asChild>
                          <button
                            type="button"
                            data-testid={CONTACT.bookDate}
                            className={`w-full flex items-center justify-between px-3 py-2 h-10 ${fieldClass} text-left`}
                          >
                            <span className={bookDate ? "text-white" : "text-white/40"}>
                              {bookDate ? format(bookDate, "PPP") : "Pick a date"}
                            </span>
                            <CalendarIcon size={16} className="text-white/50" />
                          </button>
                        </PopoverTrigger>
                        <PopoverContent className="bg-[#0C1017] border-white/10 text-white p-0" align="start">
                          <Calendar
                            mode="single"
                            selected={bookDate}
                            onSelect={setBookDate}
                            disabled={(d) => d < new Date(new Date().setHours(0, 0, 0, 0))}
                            initialFocus
                          />
                        </PopoverContent>
                      </Popover>
                    </div>
                    <div className="space-y-2">
                      <Label className={labelClass}>Preferred time</Label>
                      <Select name="time" defaultValue="11:00 AM">
                        <SelectTrigger data-testid={CONTACT.bookTime} className={fieldClass}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="bg-[#0C1017] border-white/10 text-white">
                          {times.map((s) => (
                            <SelectItem key={s} value={s} className="focus:bg-[color:var(--primary)]/10 focus:text-white">
                              {s}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label className={labelClass} htmlFor={CONTACT.bookNotes}>Notes</Label>
                    <Textarea
                      data-testid={CONTACT.bookNotes}
                      id={CONTACT.bookNotes}
                      name="notes"
                      rows={4}
                      placeholder="Tell us about your property, vision, or specific questions..."
                      className={fieldClass}
                    />
                  </div>

                  {/* Photo uploader */}
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <Label className={labelClass}>Photos (optional)</Label>
                      <span className="font-mono-accent text-[10px] tracking-[0.25em] uppercase text-white/40">
                        {bookPhotos.length}/8
                      </span>
                    </div>
                    <div
                      onClick={() => bookInputRef.current?.click()}
                      onDragOver={(e) => e.preventDefault()}
                      onDrop={(e) => { e.preventDefault(); handleFiles(e.dataTransfer.files, "book"); }}
                      className="border border-dashed border-white/15 hover:border-[color:var(--primary)]/60 bg-[#05070A] p-6 text-center cursor-pointer transition-colors"
                      data-testid="book-photo-drop"
                    >
                      <input
                        ref={bookInputRef}
                        type="file"
                        accept="image/*"
                        multiple
                        className="hidden"
                        data-testid="book-photo-input"
                        onChange={(e) => { handleFiles(e.target.files, "book"); e.target.value = ""; }}
                      />
                      {uploadingBook ? (
                        <div className="flex items-center justify-center gap-2 text-white/70 text-sm">
                          <Loader2 className="animate-spin" size={16} /> Uploading…
                        </div>
                      ) : (
                        <div className="flex flex-col items-center gap-2">
                          <ImagePlus size={22} className="text-[color:var(--primary)]" />
                          <div className="text-white/70 text-sm">Drop photos of your property or click to upload</div>
                          <div className="font-mono-accent text-[10px] tracking-[0.25em] uppercase text-white/40">
                            JPG · PNG · WEBP · Max 8MB each
                          </div>
                        </div>
                      )}
                    </div>
                    {bookPhotos.length > 0 && (
                      <div className="grid grid-cols-4 md:grid-cols-6 gap-2">
                        {bookPhotos.map((p, i) => (
                          <div key={p.filename} className="relative group aspect-square border border-white/10 overflow-hidden" data-testid={`book-photo-thumb-${i}`}>
                            <img src={p.absoluteUrl} alt="" className="w-full h-full object-cover" />
                            <button
                              type="button"
                              onClick={() => removePhoto(i, "book")}
                              className="absolute top-1 right-1 p-1 bg-black/70 border border-white/20 text-white hover:bg-[color:var(--primary)] hover:text-black transition-colors"
                              aria-label="Remove"
                              data-testid={`book-photo-remove-${i}`}
                            >
                              <X size={12} />
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  <button
                    data-testid={CONTACT.bookSubmit}
                    type="submit"
                    disabled={booking}
                    className="w-full md:w-auto inline-flex items-center gap-2 px-8 py-4 bg-[color:var(--primary)] text-black font-mono-accent text-[11px] tracking-[0.25em] uppercase hover:bg-white transition-colors btn-sharp disabled:opacity-60"
                  >
                    {booking ? <><Loader2 className="animate-spin" size={14}/> Booking</> : <>Book Consultation <CalendarIcon size={14} /></>}
                  </button>
                </form>
              </TabsContent>
            </Tabs>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
